import { useState } from 'react'

import {
  Form,
  InputLabel,
  Select,
  Space,
  TextButton,
  WithFormControlSelect,
} from '@procurify/ui'
import { useFormikContext } from 'formik'

import { PriceFormField } from '@webapp/components/PriceField'
import { Price } from '@webapp/core/components/Price'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { type Enum } from '@webapp/infrastructure/types/Enum'
import { TaxSelect } from '@webapp/platform/finance'
import { ExpenseTaxSelect, isPresetTax } from '../../ExpenseTaxSelect'
import { type ExpenseLineItemFormSchema } from '../ExpenseLineItemForm'
import messages from '../messages'

const TaxFieldsModes = {
  PRESET: 'preset',
  CUSTOM: 'custom',
} as const
type TaxFieldsMode = Enum<typeof TaxFieldsModes>

export const CUSTOM_TAX_TYPE_OPTIONS = [
  { id: '%', name: '%' },
  { id: '$', name: '$' },
]

const NO_TAX_PROFILE = { id: '%', name: '%' }

interface TaxFieldsProps {
  isSubmitting: boolean
  canOverrideTaxAmount: boolean
}

export const TaxFields = ({
  isSubmitting,
  canOverrideTaxAmount,
}: TaxFieldsProps) => {
  const intl = useProcurifyIntl()
  const formik = useFormikContext<ExpenseLineItemFormSchema>()

  const PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS = useFeatureFlag(
    'PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS'
  )

  const isCustomTaxSet =
    (formik.values.taxType?.id === '$' && (formik.values.taxAmount ?? 0) > 0) ||
    (formik.values.taxType?.id === '%' &&
      formik.values.customTaxPercentage != null)
  const [activeMode, setActiveMode] = useState<TaxFieldsMode>(
    isCustomTaxSet ? TaxFieldsModes.CUSTOM : TaxFieldsModes.PRESET
  )

  /**
   * To be removed with PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
   */
  const isTaxPresetSelected = isPresetTax(formik.values.taxType)

  const hasTaxProfile = typeof formik.values.taxType?.id === 'number'
  const isCustomDollar = formik.values.taxType?.id === '$'
  const hasCustomPercentage = formik.values.customTaxPercentage != null
  const currencyName = formik.values.currency?.name ?? ''

  const isTaxAmountOverrideEnabled =
    canOverrideTaxAmount &&
    (activeMode === TaxFieldsModes.PRESET ? hasTaxProfile : hasCustomPercentage)

  const handleTaxAmountBlur = () => {
    if (isTaxAmountOverrideEnabled && formik.values.taxAmount == null) {
      formik.setFieldValue('taxAmount', 0)
    }
  }

  const renderTaxAmountRow = () => (
    <Space align='center' direction='horizontal' justify='end'>
      <InputLabel htmlFor='taxAmount'>
        {isTaxAmountOverrideEnabled
          ? intl.formatMessage(messages.taxAmountWithCurrencyLabel, {
              currencyName,
            })
          : intl.formatMessage(messages.taxAmountLabel)}
      </InputLabel>
      <Space.Item width={1 / 3}>
        {isTaxAmountOverrideEnabled ? (
          <PriceFormField
            id='taxAmount'
            name='taxAmount'
            disabled={isSubmitting}
            decimalPlaces={2}
            onBlur={handleTaxAmountBlur}
            inputProps={{
              allowNegative: false,
              style: { textAlign: 'right' },
            }}
          />
        ) : (
          <Price
            textAlign='right'
            price={formik.values.taxAmount ?? 0}
            currency={formik.values.currency?.name}
            fontSize='small'
          />
        )}
      </Space.Item>
    </Space>
  )

  return PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS ? (
    <>
      {activeMode === TaxFieldsModes.PRESET && (
        <>
          <Space align='center' direction='horizontal' justify='end'>
            <InputLabel>{intl.formatMessage(messages.taxLabel)}</InputLabel>
            <Space.Item width={2 / 3}>
              <TaxSelect
                aria-label={intl.formatMessage(messages.taxTypeAriaLabel)}
                inputId='taxType-select'
                value={
                  typeof formik.values.taxType?.id === 'number'
                    ? formik.values.taxType
                    : null
                }
                onChange={(opt) => {
                  formik.setFieldValue('taxType', opt ?? NO_TAX_PROFILE)
                  if (!opt || typeof opt.id !== 'number') {
                    formik.setFieldValue('taxAmount', null)
                  }
                }}
                isDisabled={isSubmitting}
                menuPortalTarget={document.body}
                menuPlacement='auto'
                placeholder={intl.formatMessage(
                  messages.taxSelectPlaceholderText
                )}
                isClearable
              />
            </Space.Item>
          </Space>
          <Space direction='horizontal' justify='end'>
            <TextButton
              type='button'
              onClick={() => {
                formik.setFieldValue('taxType', { id: '%', name: '%' })
                formik.setFieldValue('customTaxPercentage', null)
                formik.setFieldValue('taxAmount', null)
                setActiveMode(TaxFieldsModes.CUSTOM)
              }}
            >
              {intl.formatMessage(messages.enterCustomTaxButtonLabel)}
            </TextButton>
          </Space>
          {renderTaxAmountRow()}
        </>
      )}
      {activeMode === TaxFieldsModes.CUSTOM && (
        <>
          <Space align='center' direction='horizontal' justify='end'>
            <InputLabel>{intl.formatMessage(messages.taxLabel)}</InputLabel>
            <Space.Item width={1 / 3}>
              {isCustomDollar ? (
                <PriceFormField
                  id='taxAmount'
                  name='taxAmount'
                  aria-label={intl.formatMessage(messages.taxAmountLabel)}
                  disabled={isSubmitting}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              ) : (
                <Form.NumberField
                  id='customTaxPercentage'
                  name='customTaxPercentage'
                  hideHelperText
                  disabled={isSubmitting}
                  placeholder='0'
                  inputProps={{
                    allowNegative: false,
                    suffix: ' %',
                    style: { textAlign: 'right' },
                  }}
                />
              )}
            </Space.Item>
            <Space.Item width={1 / 3}>
              <Select
                aria-label={intl.formatMessage(messages.taxTypeAriaLabel)}
                inputId='taxType-select'
                options={CUSTOM_TAX_TYPE_OPTIONS}
                value={
                  typeof formik.values.taxType?.id === 'number'
                    ? CUSTOM_TAX_TYPE_OPTIONS[0]
                    : formik.values.taxType
                }
                onChange={(opt) => {
                  formik.setFieldValue('taxType', opt)
                  formik.setFieldValue('customTaxPercentage', null)
                  formik.setFieldValue('taxAmount', null)
                }}
                getOptionLabel={(opt) => opt.name}
                getOptionValue={(opt) => opt.id.toString()}
                isDisabled={isSubmitting}
                menuPortalTarget={document.body}
                menuPlacement='auto'
              />
            </Space.Item>
          </Space>
          <Space direction='horizontal' justify='end'>
            <TextButton
              type='button'
              onClick={() => {
                formik.setFieldValue('taxType', NO_TAX_PROFILE)
                formik.setFieldValue('customTaxPercentage', null)
                formik.setFieldValue('taxAmount', null)
                setActiveMode(TaxFieldsModes.PRESET)
              }}
            >
              {intl.formatMessage(messages.useTaxPresetButtonText)}
            </TextButton>
          </Space>
          {!isCustomDollar && renderTaxAmountRow()}
        </>
      )}
    </>
  ) : (
    <Space align='center' direction='horizontal' justify='end'>
      <InputLabel htmlFor='taxAmount'>
        {intl.formatMessage(messages.taxAmountLabel)}
      </InputLabel>
      <Space.Item width={1 / 3}>
        <PriceFormField
          id='taxAmount'
          name='taxAmount'
          disabled={isSubmitting || isTaxPresetSelected}
          inputProps={{ style: { textAlign: 'right' } }}
        />
      </Space.Item>
      <Space.Item width={1 / 3}>
        <WithFormControlSelect id='taxType' name='taxType'>
          <ExpenseTaxSelect
            aria-label={intl.formatMessage(messages.taxTypeAriaLabel)}
            inputId='taxType-select'
            value={formik.values.taxType}
            onChange={(opt) => formik.setFieldValue('taxType', opt)}
            isDisabled={isSubmitting}
            menuPortalTarget={document.body}
            menuPlacement='auto'
          />
        </WithFormControlSelect>
      </Space.Item>
    </Space>
  )
}
